import React from 'react';
import { visibilityBand } from '@/utils/visibilityBands';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, Moon, Clock, Camera, Compass, ArrowUp } from 'lucide-react';
import { FullMoonData } from '@/utils/moonCalculations';

interface BestFullMoonsProps {
  fullMoons: FullMoonData[];
  month: number;
  year: number;
}

const BestFullMoons: React.FC<BestFullMoonsProps> = ({ fullMoons, month, year }) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Was a sixth copy of the ladder, and it had no floor: a rating of 0 read "Fair".
  const getQualityLabel = (rating: number) => {
    const label = visibilityBand(rating);
    if (rating >= 8) return { label, color: 'bg-green-500' };
    if (rating >= 6) return { label, color: 'bg-gold' };
    if (rating >= 4) return { label, color: 'bg-yellow-500' };
    return { label, color: 'bg-orange-500' };
  };

  const VisibilityStars = ({ rating }: { rating: number }) => {
    return (
      <div className="flex gap-1">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < Math.ceil(rating / 2) 
                ? 'fill-galaxy-blue text-galaxy-blue' 
                : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
    );
  };

  if (fullMoons.length === 0) {
    return (
      <Card className="glass-card border-border/50 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center text-foreground font-light text-xl">
            <Moon className="mr-3 text-galaxy-blue w-6 h-6" />
            Best Moon Photography Nights
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <div className="text-muted-foreground">
            <Moon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg mb-2">No full moons this month</p>
            <p className="text-sm">
              Check adjacent months for full moon photography opportunities.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-border/50 shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center text-foreground font-light text-xl">
          <Camera className="mr-3 text-galaxy-blue w-6 h-6" />
          Best Moon Photography Nights
          <Badge variant="outline" className="ml-3 text-galaxy-blue border-galaxy-blue">
            {monthNames[month]} {year}
          </Badge>
        </CardTitle>
        <p className="text-muted-foreground text-sm mt-2">
          Full and near-full moons ranked for moon photography
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {fullMoons.slice(0, 5).map((night, index) => {
          const quality = getQualityLabel(night.rating);
          
          return (
            <div key={night.date} className="relative">
              <div className="flex items-center justify-between p-3 md:p-4 rounded-lg bg-secondary/20 border border-border/30 hover:bg-secondary/30 transition-colors">
                <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="text-lg md:text-2xl font-bold text-galaxy-blue">
                      #{index + 1}
                    </div>
                    <div className="hidden sm:block">
                      <VisibilityStars rating={night.rating} />
                    </div>
                  </div>
                  
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="font-semibold text-foreground text-sm md:text-base truncate flex items-center gap-2">
                      {night.fullDate}
                      {night.isSupermoon && (
                        <Badge className="bg-yellow-500 text-white text-xs">
                          ✨ Supermoon
                        </Badge>
                      )}
                    </div>
                    
                    {/* Moonrise/set with azimuth */}
                    <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-muted-foreground mt-1">
                      <div className="flex items-center gap-1">
                        <Moon className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                        <span>Rise: {night.moonrise}</span>
                        <Compass className="w-3 h-3" />
                        <span className="text-galaxy-blue">{night.moonriseAzimuth}</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-muted-foreground mt-1">
                      <div className="flex items-center gap-1">
                        <Moon className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0 opacity-50" />
                        <span>Set: {night.moonset}</span>
                        <Compass className="w-3 h-3" />
                        <span className="text-galaxy-blue">{night.moonsetAzimuth}</span>
                      </div>
                    </div>
                    
                    {/* Max altitude */}
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <ArrowUp className="w-3 h-3" />
                      <span>Max: {night.maxAltitude}° at {night.maxAltitudeTime}</span>
                    </div>
                    
                    {/* Twilight note */}
                    {night.twilightNote && (
                      <div className="text-xs text-amber-600 mt-1">
                        🌅 {night.twilightNote}
                      </div>
                    )}
                    
                    <div className="sm:hidden mt-1">
                      <VisibilityStars rating={night.rating} />
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                  <div className="text-center flex-shrink-0">
                    <div className="text-lg md:text-2xl">{night.moonPhaseEmoji}</div>
                    <div className="text-xs text-muted-foreground">{night.illumination}%</div>
                  </div>
                  
                  <Badge className={`${quality.color} text-white text-xs md:text-sm flex-shrink-0 hidden sm:inline-flex`}>
                    {quality.label}
                  </Badge>
                  
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-galaxy-blue text-sm md:text-base">
                      {night.rating}/10
                    </div>
                    <div className="text-xs text-muted-foreground">rating</div>
                  </div>
                </div>
              </div>
              
              {/* Mobile quality badge */}
              <div className="sm:hidden mt-2 flex justify-center">
                <Badge className={`${quality.color} text-white text-xs`}>
                  {quality.label}
                </Badge>
              </div>
              
              {index === 0 && night.isSupermoon && (
                <div className="absolute -top-2 -right-2">
                  <div className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-lg">
                    ⭐ Best Night
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        <div className="mt-6 p-4 bg-galaxy-blue/10 rounded-lg border border-galaxy-blue/20">
          <h4 className="font-semibold text-galaxy-blue mb-2 flex items-center">
            <Camera className="w-4 h-4 mr-2" />
            Moon Photography Tips
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Scout your location in advance to find interesting foreground elements</li>
            <li>• Use the azimuth to position yourself for moonrise/moonset alignment shots</li>
            <li>• Blue hour offers the best balance of ambient light and moon brightness</li>
            <li>• Supermoons appear ~14% larger - perfect for dramatic silhouette shots</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default BestFullMoons;
